import { ReactNode } from 'react';
import {
  Box,
  Card,
  Divider,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { widgetTokens } from '../designTokens';

export interface DashboardWidgetProps {
  title: string;
  icon?: ReactNode;
  iconBg?: string;
  /**
   * Retained for API compatibility but no longer rendered — the prototype's
   * PanelCard uses a neutral surface with no coloured top strip, and the
   * tile's information should drive its identity rather than a decorative
   * accent bar. Removed visually as part of the Feature 02 Phase 3 visual
   * alignment pass. Callers can keep passing the prop; it's a no-op.
   */
  accentColor?: string;
  /** Inline CTA rendered on the right of the title row. */
  cta?: { label: string; href?: string; onClick?: () => void };
  /** Shown when loading is true and no data is yet present. */
  loading?: boolean;
  /** Shown inline (above children) when a non-blocking error is present. */
  error?: string | null;
  /** Show a centred empty-state message instead of children. */
  empty?: boolean;
  emptyMessage?: string;
  children?: ReactNode;
  /** Optional test id to target this widget in Vitest. */
  'data-testid'?: string;
}

/**
 * Shared card shell for every Dashboard widget. Gives widgets a consistent
 * header (title + icon + CTA) plus loading / error / empty treatments so the
 * individual widget components stay focused on data rendering.
 */
export function DashboardWidget({
  title,
  icon: _icon,
  iconBg: _iconBg = 'action.hover',
  // accentColor intentionally ignored — see prop docstring.
  cta,
  loading = false,
  error = null,
  empty = false,
  emptyMessage = 'Nothing to show right now.',
  children,
  'data-testid': testId,
}: DashboardWidgetProps) {
  return (
    <Card
      variant="outlined"
      sx={{
        // Border-radius left at the MUI Card default (theme shape = 8px) so
        // these cards match the sibling `PanelCard` widgets; the old 16px
        // override made their corners visibly rounder than everything else.
        minHeight: widgetTokens.card.minHeight,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          // Subtle lift on hover so cards feel interactive without looking like
          // full-click targets (the CTA link remains the primary affordance).
          transform: 'translateY(-1px)',
          boxShadow: 2,
        },
      }}
      data-testid={testId}
    >
      {/* Header + divider geometry is identical to the sibling `PanelCard`
          shell (px18 / py14 / minHeight48, h2 at lineHeight 1.4, full-width
          divider) so every dashboard card's title line and separator sit at
          the same position. `icon` / `iconBg` are kept on the prop type for
          API compatibility — they're a no-op visually. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: '18px',
          py: '14px',
          minHeight: 48,
        }}
      >
        <Box
          component="h2"
          sx={{
            m: 0,
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.4,
            minWidth: 0,
          }}
        >
          {title}
        </Box>
        {cta ? (
          // Anchor at zero height (centred on the title) so the CTA can't
          // inflate the header — see the matching note in `PanelCard`.
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', height: 0 }}>
            {cta.href ? (
              <Link href={cta.href} underline="hover" sx={{ fontSize: 13 }}>
                {cta.label}
              </Link>
            ) : (
              <Link
                component="button"
                type="button"
                onClick={cta.onClick}
                underline="hover"
                sx={{ fontSize: 13 }}
              >
                {cta.label}
              </Link>
            )}
          </Box>
        ) : null}
      </Box>
      <Divider />

      <Box sx={{ p: '18px' }}>
        {loading ? (
          <Stack spacing={1}>
            <Skeleton variant="text" height={24} />
            <Skeleton variant="text" height={24} width="85%" />
            <Skeleton variant="text" height={24} width="60%" />
          </Stack>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : empty ? (
          <Box sx={widgetTokens.emptyState}>
            <Typography variant="body2">{emptyMessage}</Typography>
          </Box>
        ) : (
          children
        )}
      </Box>
    </Card>
  );
}
